import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';
import {
  parseTemplateDutyPointCode,
  parseTemplateDutyPointKey,
  templateDutyPointLabelOf,
  templateDutyPointRowsOf,
  type TemplateDecisionContextDto,
  type TemplateDecisionRowDto,
} from '@pams/shared';
import { decisionInTemplateHref } from '@/lib/templateLinks';
import { DECISIONS_COPY, TEMPLATE_VIEW_COPY } from '../copy';
import { totalsColumnsOf } from './decisionContext';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [TEMPLATE-FIND] WHAT THE BOOKLET SAYS ABOUT ONE DECISION — the muted lines under a decision's heading,
 *  rendered by the Decisions page's row and by the library's preview alike, so the two cannot disagree.
 *
 *  ⚠️ A PRINTED TOTAL SAYS WHAT TO CHECK, NOT ONLY THAT SOMETHING DIFFERS. Its lines name the template, the
 *  rows the booklet's Total row adds up (`table 1, rows 1–4`, never a key), each column that differs in
 *  words AND weight (`data-differs`, the template pane's own rule — colour alone says nothing), the columns
 *  that agree on one line, and the ones with no printed figure.
 *
 *  ⚠️ A DUTY POINT SAYS WHERE IT IS PRINTED AND WHAT PAMS COULD NOT READ — the template and its page, the
 *  issue, and every unread cell in the template pane's own words (`Not read: Inspr.: 34. Goranhata St. …`),
 *  which often hold the very place the form asks for. Its notes follow, muted.
 *
 *  ⚠️ BOTH THEN CARRY ONE LINK — "Show it in template t152", to that check or that row, highlighted — and
 *  nothing else: no Tooltip (the overlay census does not move), no `scrollIntoView` (the template pane
 *  scrolls its own target into view) and no request (`LibraryIndex` resolves the number from the list the
 *  library already reads). Every other class renders the lines it always did.
 *
 *  ⚠️ [TEMPLATE-FIND] THAT LINK'S NAME TELLS APART TWO ROWS WHOSE LABELS MATCH. Two printed totals on one page
 *  share `t36 · page 78` (`p78:t36`, `p78:t36#2`), and a serial the booklet printed twice gives two duty points
 *  `t3 · (4)` — so the name adds the rows a total adds up, or a serial duty point's table and row
 *  (`showInTemplateLabel`). A duty point with no serial is labelled by its table and row already, and adds nothing.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
type TotalsDecisionContext = Extract<TemplateDecisionContextDto, { class: 'TOTALS' }>;
type DutyPointDecisionContext = Extract<TemplateDecisionContextDto, { class: 'DUTY_POINT' }>;

const plain = (lines: readonly ReactNode[]): ReactElement[] =>
  lines.map((line, index) => <li key={`context-${String(index)}`}>{line}</li>);

/* The lines every class but a printed total and a duty point has always shown — moved from `DecisionRow`. */
const sharedLines = (
  context: Exclude<TemplateDecisionContextDto, TotalsDecisionContext | DutyPointDecisionContext>,
): ReactNode[] => {
  switch (context.class) {
    case 'SOURCE':
      return [
        DECISIONS_COPY.sourceUsedFor[context.usedFor],
        DECISIONS_COPY.sourcePeople(context.people),
        ...(context.bundleStatus ? [DECISIONS_COPY.bundleStatus(context.bundleStatus)] : []),
      ];
    case 'FORCE_COLUMN':
      return [DECISIONS_COPY.columnPeople(context.people)];
    case 'POLICY':
      return [
        `${DECISIONS_COPY.policyColumns}: ${context.columns.map((column) => column.name).join(', ') || '—'}`,
        DECISIONS_COPY.policyDutyPoints(context.dutyPoints),
      ];
    case 'IN_CHARGE_ROLE':
      return [`${context.label} · ${context.policyName}`];
    case 'SCHEDULE':
      return [
        TEMPLATE_VIEW_COPY.pages(context.pages),
        ...(context.roundTheClock ? [DECISIONS_COPY.roundTheClock] : []),
      ].filter((line) => line !== '');
  }
};

const totalsLines = (context: TotalsDecisionContext): ReactElement[] => {
  const { differs, matches, notPrinted } = totalsColumnsOf(context);
  const rows = templateDutyPointRowsOf(context.dutyPoints.map((point) => point.key));
  return [
    ...plain([
      ...(context.templateTitle === '' ? [] : [context.templateTitle]),
      rows === '' ? DECISIONS_COPY.totalsCoversUnknown : DECISIONS_COPY.totalsCovers(context.page, rows),
    ]),
    /* ⚠️ Each differing column on its own line, in weight AND words — the figure a person checks on the page. */
    ...differs.map((column) => (
      <li
        key={`differs-${column.column}`}
        className="font-medium text-foreground"
        data-differs="true"
        data-column={column.column}
      >
        {/* ⚠️ The in-charge cells qualify the figure READ, never the difference — the copy places them. */}
        {DECISIONS_COPY.totalsDiffers(column.column, column.printed, column.parsed, column.inCharge)}
      </li>
    )),
    ...(matches.length === 0
      ? []
      : [
          <li key="matches" data-testid="decision-totals-matches">
            {DECISIONS_COPY.totalsMatch(matches.map((column) => `${column.column} ${String(column.printed)}`))}
          </li>,
        ]),
    ...(notPrinted.length === 0
      ? []
      : [
          <li key="not-printed" data-testid="decision-totals-not-printed">
            {DECISIONS_COPY.totalsNotPrinted(notPrinted.map((column) => column.column))}
          </li>,
        ]),
  ];
};

const dutyPointLines = (context: DutyPointDecisionContext): ReactElement[] => [
  ...plain([
    [context.templateTitle, DECISIONS_COPY.page(context.page)].filter((part) => part !== '').join(' · '),
    DECISIONS_COPY.dpIssue[context.issue],
    ...(context.place ? [context.place] : []),
  ]),
  ...context.unparsedCells.map((cell, index) => (
    <li key={`unread-${String(index)}`} data-testid="decision-unread">
      {TEMPLATE_VIEW_COPY.unread(cell)}
    </li>
  )),
  ...context.notes.map((note, index) => (
    <li key={`note-${String(index)}`} className="text-xs" data-testid="decision-note">
      {note}
    </li>
  )),
];

/**
 * What tells this row's link apart from another with the same label — `adding up table 1, rows 1–4` for a printed
 * total, `table 1, row 7` for a duty point the booklet gave a serial — or `null` where the label already says it.
 * ⚠️ A duty point's table and row come from its CODE, through the shared parsers, never from the label; a key that
 * is not a position names nothing a person reads, so it adds nothing rather than a raw key.
 */
const showInTemplateWhereOf = (row: TemplateDecisionRowDto): string | null => {
  const { context } = row;
  if (context.class === 'TOTALS') {
    const rows = templateDutyPointRowsOf(context.dutyPoints.map((point) => point.key));
    return rows === '' ? null : DECISIONS_COPY.addingUp(rows);
  }
  if (context.class === 'DUTY_POINT' && context.sl !== null && context.sl !== '') {
    const dutyPointKey = parseTemplateDutyPointCode(row.code)?.dutyPointKey ?? '';
    return parseTemplateDutyPointKey(dutyPointKey) === null
      ? null
      : templateDutyPointLabelOf({ key: dutyPointKey, sl: null });
  }
  return null;
};

export const DecisionContext = ({
  row,
  setKey,
}: {
  row: TemplateDecisionRowDto;
  setKey: string;
}): React.ReactElement => {
  const { context } = row;
  const lines =
    context.class === 'TOTALS'
      ? totalsLines(context)
      : context.class === 'DUTY_POINT'
        ? dutyPointLines(context)
        : plain(sharedLines(context));
  const href = decisionInTemplateHref(setKey, row);
  const key = context.class === 'TOTALS' || context.class === 'DUTY_POINT' ? context.templateKey : '';

  return (
    <>
      <ul className="flex flex-col gap-0.5 text-muted-foreground" data-testid="decision-context">
        {lines}
      </ul>
      {href === null || key === '' ? null : (
        <p>
          <Link
            to={href}
            className="text-link underline-offset-4 hover:underline"
            data-testid="decision-show-in-template"
            aria-label={DECISIONS_COPY.showInTemplateLabel(key, row.label, showInTemplateWhereOf(row))}
          >
            {DECISIONS_COPY.showInTemplate(key)}
          </Link>
        </p>
      )}
    </>
  );
};
